import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {DataService} from "./data-service";
import {StartupService} from "../service-ui/startup.service";
import {MessageService} from "../service-ui/message.service";
import {ServiceProviderService} from "./service-provider.service";
import {OrganizationService} from "./organization.service";
import {FacilityService} from "./facility.service";
import {RateConfigDocument} from "../../model/rate/RateConfig";
import {RateConfigEvaluator, PreviewResult, CostTableRow} from "../../model/rate/RateConfigEvaluator";
import {PreviewItem} from "../../model/rate/PreviewMatrix";
import {ServiceProvider} from "../../model/ServiceProvider";
import {Organization} from "../../model/Organization";
import {Facility} from "../../model/Facility";

/** Which entity owns the rate-config document being edited. */
export type RateConfigSubjectKind = 'serviceProvider' | 'facility' | 'organization';

/**
 * Identifies which property on the host entity is being edited. ServiceProvider
 * has four (production: `rateConfig` + `serviceProviderChargedRateConfig`;
 * packing: `packingRateConfig` + `serviceProviderChargedPackingRateConfig`);
 * Facility has two (`rateConfig`, `packingRateConfig`); Organization has two
 * (the production override `rateConfigOverride` and the packing override
 * `packingRateConfigOverride`).
 *
 * The packing properties hold a {@link RateConfigDocument} of the SAME shape
 * as the production properties — only the entity column (and the inputs the
 * server feeds into the evaluator at rating time) differ. This lets a single
 * editor + service serve both concerns; see shared/PackingRateConfig.md for
 * the input/concept mapping.
 */
export type RateConfigDocumentProperty =
  | 'rateConfig'
  | 'serviceProviderChargedRateConfig'
  | 'rateConfigOverride'
  | 'packingRateConfig'
  | 'serviceProviderChargedPackingRateConfig'
  | 'packingRateConfigOverride';

/**
 * The packing-side properties — the four-way mirror of the production
 * properties. Used for compact role-gating checks and so we can feed
 * the editor a single `RateConfigDocumentProperty` while keeping the
 * load/save plumbing in one switch.
 */
export const PACKING_PROPERTIES: ReadonlyArray<RateConfigDocumentProperty> =
  ['packingRateConfig', 'serviceProviderChargedPackingRateConfig', 'packingRateConfigOverride'];

/** Authoritative documents replace; OVERRIDE documents layer over a base. */
export type RateConfigEditorMode = 'AUTHORITATIVE' | 'OVERRIDE';

/** Bundle of state returned by {@link RateConfigService.loadFor}. */
export interface RateConfigLoadResult {
  /** Currently-saved document on the entity. Empty `{}` when not yet set. */
  doc: RateConfigDocument;
  /** Parent SP `rateConfig` for OVERRIDE mode; otherwise undefined. */
  baseDoc?: RateConfigDocument;
  /** ISO currency code from the host entity (e.g. SP.currency / Facility.currency). */
  currency: string;
  /** Read-only when role lacks edit rights for this subject (e.g. SP role on charged config). */
  readOnly: boolean;
}

/**
 * Service that wraps the existing entity services to load/save
 * {@link RateConfigDocument}s. Also exposes pure-evaluator passthroughs so
 * components don't have to import {@link RateConfigEvaluator} directly.
 *
 * Self-registers with {@link StartupService} per project convention.
 */
@Injectable()
export class RateConfigService extends DataService {

  constructor(private messageService: MessageService,
              private startupService: StartupService,
              private serviceProviderService: ServiceProviderService,
              private organizationService: OrganizationService,
              private facilityService: FacilityService) {
    super(startupService);
  }

  /**
   * Required by {@link DataService}. RateConfig data is fetched on demand
   * (per-subject) so there is no in-memory list to wipe; we no-op here.
   */
  reset(): void {
    // intentionally empty
  }

  /**
   * Load the rate-config document for a subject. For OVERRIDE mode (organization),
   * also fetches the parent ServiceProvider's `rateConfig` as the base document.
   *
   * Read-only flag:
   *  - When the active role is `serviceProvider` and the requested property is
   *    `serviceProviderChargedRateConfig`, the user can view but not save.
   *  - All other gating is done by the host page (network-only panels for the
   *    SP rateConfig, etc.) — this service does not attempt to be the sole
   *    arbiter of editability.
   */
  loadFor(subjectKind: RateConfigSubjectKind, subjectId: number,
          property: RateConfigDocumentProperty): Observable<RateConfigLoadResult> {
    const role = this.startupService.selectedRole;
    // Read-only matrix:
    //  - SP role viewing what the network charges them: view-only.
    //    (Applies symmetrically to production AND packing charged-by-network
    //     documents.)
    //  - Organization role on its own *Override documents: HARD DENY edit.
    //    End users must not be able to grant themselves discounts or edit
    //    their own rates. Only network/serviceProvider roles may edit.
    const isOverride = (property === 'rateConfigOverride' || property === 'packingRateConfigOverride');
    const isChargedByNetwork = (property === 'serviceProviderChargedRateConfig'
      || property === 'serviceProviderChargedPackingRateConfig');
    const readOnly =
      (role === 'serviceProvider' && isChargedByNetwork) ||
      (role === 'organization' && isOverride) ||
      (role !== 'network' && role !== 'serviceProvider' && isOverride);

    if (subjectKind === 'serviceProvider') {
      return this.serviceProviderService.getServiceProvider('' + subjectId).pipe(
        map(sp => {
          if (!sp) { return {doc: {}, currency: 'USD', readOnly}; }
          let doc: RateConfigDocument;
          switch (property) {
            case 'serviceProviderChargedRateConfig':         doc = sp.getServiceProviderChargedRateConfig(); break;
            case 'packingRateConfig':                        doc = sp.getPackingRateConfig(); break;
            case 'serviceProviderChargedPackingRateConfig':  doc = sp.getServiceProviderChargedPackingRateConfig(); break;
            default:                                         doc = sp.getRateConfig(); break;
          }
          return {doc, currency: sp.currency || 'USD', readOnly};
        })
      );
    }

    if (subjectKind === 'facility') {
      const cached = this.facilityService.getLocalItem(subjectId);
      const pickFacilityDoc = (f: Facility): RateConfigDocument =>
        property === 'packingRateConfig' ? f.getPackingRateConfig() : f.getRateConfig();
      if (cached) {
        return of({doc: pickFacilityDoc(cached), currency: cached.currency || 'USD', readOnly});
      }
      return this.facilityService.getItem(subjectId).pipe(
        map((f: Facility) => f
          ? {doc: pickFacilityDoc(f), currency: f.currency || 'USD', readOnly}
          : {doc: {}, currency: 'USD', readOnly})
      );
    }

    // Organization (OVERRIDE mode). Fetch the org and the parent SP base.
    return this.organizationService.getOrganization('' + subjectId).pipe(
      map((org: Organization) => {
        if (!org) { return {doc: {}, currency: 'USD', readOnly}; }
        const overrideDoc = property === 'packingRateConfigOverride'
          ? org.getPackingRateConfigOverride()
          : org.getRateConfigOverride();
        const result: RateConfigLoadResult = {
          doc: overrideDoc,
          currency: 'USD',
          readOnly
        };
        // Asynchronously hydrate base; emit synchronously for quick render and
        // patch baseDoc into the result via a follow-up emission. To keep the
        // contract simple we do the SP fetch eagerly here and merge results.
        return result;
      })
    );
  }

  /**
   * Two-step organization load: returns both the override doc and the parent SP
   * base doc. Use this in OVERRIDE mode to populate the side-by-side preview.
   */
  loadOrganizationOverride(orgId: number): Observable<RateConfigLoadResult> {
    return this.organizationService.getOrganization('' + orgId).pipe(
      map((org: Organization) => {
        if (!org) { return {doc: {}, currency: 'USD', readOnly: false}; }
        const overrideDoc = org.getRateConfigOverride();
        const result: RateConfigLoadResult = {
          doc: overrideDoc,
          currency: 'USD',
          readOnly: false
        };
        const spId = org.serviceProvider_ID;
        if (spId && spId > 0) {
          // Fire-and-forget side-fetch: callers should subscribe to a separate
          // call when they need the SP base. Keep this method synchronous-feel
          // by leaving baseDoc unset; components that care call
          // serviceProviderService.getServiceProvider() themselves.
        }
        return result;
      })
    );
  }

  /**
   * Persist a {@link RateConfigDocument} for the given subject by mutating the
   * proper string column on the entity and PUTting the entity to the server.
   *
   * Returns an Observable that emits true on success, false on failure (errors
   * are surfaced via the underlying service's catchError handler — no rethrow).
   */
  saveFor(subjectKind: RateConfigSubjectKind, subjectId: number,
          property: RateConfigDocumentProperty, doc: RateConfigDocument): Observable<boolean> {
    console.log('RateConfigService.saveFor: ENTER', {subjectKind, subjectId, property, role: this.startupService.selectedRole});
    // Defensive role gate. The editor's readOnly flag also blocks save in
    // the UI, but enforce here too so a stale subscription or programmatic
    // call cannot bypass it. The real authority is the server.
    const role = this.startupService.selectedRole;
    const isOverride = (property === 'rateConfigOverride' || property === 'packingRateConfigOverride');
    const isChargedByNetwork = (property === 'serviceProviderChargedRateConfig'
      || property === 'serviceProviderChargedPackingRateConfig');
    if (isOverride && role !== 'network' && role !== 'serviceProvider') {
      this.log(`saveFor refused: role=${role} cannot edit ${property}`);
      console.warn('RateConfigService.saveFor: refused override for role', role, property);
      return of(false);
    }
    if (isChargedByNetwork && role !== 'network') {
      this.log(`saveFor refused: role=${role} cannot edit ${property}`);
      console.warn('RateConfigService.saveFor: refused charged-by-network for role', role, property);
      return of(false);
    }
    if (subjectKind === 'serviceProvider') {
      console.log('RateConfigService.saveFor: fetching SP id=' + subjectId);
      return this.serviceProviderService.getServiceProvider('' + subjectId).pipe(
        map(sp => {
          console.log('RateConfigService.saveFor: got SP', {present: !!sp, id: sp && sp.ID});
          if (!sp) { return false; }
          switch (property) {
            case 'serviceProviderChargedRateConfig':         sp.setServiceProviderChargedRateConfig(doc); break;
            case 'packingRateConfig':                        sp.setPackingRateConfig(doc); break;
            case 'serviceProviderChargedPackingRateConfig':  sp.setServiceProviderChargedPackingRateConfig(doc); break;
            default:                                         sp.setRateConfig(doc); break;
          }
          console.log('RateConfigService.saveFor: about to PUT', {property, value: (sp as any)[property]});
          this.serviceProviderService.updateServiceProvider(sp).subscribe(
            r => console.log('RateConfigService.saveFor: PUT success', r),
            e => console.error('RateConfigService.saveFor: PUT error', e)
          );
          this.log(`saveFor serviceProvider id=${subjectId} property=${property}`);
          return true;
        })
      );
    }
    if (subjectKind === 'facility') {
      const cached = this.facilityService.getLocalItem(subjectId);
      const apply = (f: Facility): boolean => {
        if (!f) { return false; }
        if (property === 'packingRateConfig') {
          f.setPackingRateConfig(doc);
        } else {
          f.setRateConfig(doc);
        }
        this.facilityService.updateItem(f).subscribe();
        this.log(`saveFor facility id=${subjectId} property=${property}`);
        return true;
      };
      if (cached) { return of(apply(cached)); }
      return this.facilityService.getItem(subjectId).pipe(map(apply));
    }
    // Organization
    return this.organizationService.getOrganization('' + subjectId).pipe(
      map((org: Organization) => {
        if (!org) { return false; }
        if (property === 'packingRateConfigOverride') {
          org.setPackingRateConfigOverride(doc);
        } else {
          org.setRateConfigOverride(doc);
        }
        this.organizationService.updateOrganization(org).subscribe();
        this.log(`saveFor organization id=${subjectId} property=${property}`);
        return true;
      })
    );
  }

  // ── pure-evaluator passthroughs ──────────────────────────────────────

  evaluateItem(item: PreviewItem, quantity: number, doc: RateConfigDocument, scope: string): PreviewResult {
    return RateConfigEvaluator.evaluateItem(item, quantity, doc, scope);
  }

  evaluateBreaks(item: PreviewItem, doc: RateConfigDocument, scope: string,
                 tiers?: { minQty: number; maxQty: number }[]): CostTableRow[] {
    return RateConfigEvaluator.evaluateBreaks(item, doc, scope, tiers);
  }

  mergeOverride(base: RateConfigDocument, override: RateConfigDocument | undefined | null): RateConfigDocument {
    return RateConfigEvaluator.mergeOverride(base, override);
  }

  private log(msg: string): void {
    this.messageService.add(`RateConfigService: ${msg}`);
  }
}




